import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress } from '@material-ui/core'
import useBlueprintData from './hooks/useBlueprintData'
import { LATCH_LATEST } from './constants'
import AxisChartLayout from './chartLayouts/AxisChartLayout'
import PieChartLayout from './chartLayouts/PieChartLayout'
import TreeMapLayout from './chartLayouts/TreeMapLayout'
import FunnelChartLayout from './chartLayouts/FunnelChartLayout'
import RingChartLayout from './chartLayouts/RingChartLayout'

const LAYOUT_RENDERERS = {
  axis: AxisChartLayout,
  pie: PieChartLayout,
  treemap: TreeMapLayout,
  funnel: FunnelChartLayout,
  ring: RingChartLayout
}

/**
 * Fetches data for the primary purpose and resolves latch parameters.
 * Layout renderers may call useBlueprintData again for per-series overrides.
 */
function usePrimaryBlueprintData (blueprintCode, config, latch, parameters) {
  const { purposeCode } = config
  const isConfigured = !!purposeCode

  const effectiveParameters = useMemo(() => {
    if (!latch) return parameters
    return { ...parameters, [latch.parameterName]: LATCH_LATEST }
  }, [parameters, latch])

  const { data, isLoading, error } = useBlueprintData(
    blueprintCode,
    isConfigured ? purposeCode : null,
    { parameters: effectiveParameters, pageSize: 1000 }
  )

  return { data, isLoading, error, effectiveParameters }
}

function BlueprintChart ({ blueprintCode, config, latch, parameters }) {
  const { layout } = config

  const LayoutRenderer = LAYOUT_RENDERERS[layout]

  const { data, isLoading, error, effectiveParameters } = usePrimaryBlueprintData(
    blueprintCode, config, latch, parameters
  )

  if (!LayoutRenderer) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-warning-500)' }}>
        Unknown chart layout &quot;{layout}&quot;
      </div>
    )
  }

  if (!config.purposeCode) {
    return null
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: config.height || 400 }}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-danger-500)' }}>
        Chart error: {error.message ?? 'Unknown error'}
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: config.height || 400,
        color: 'var(--text-color-subtle)'
      }}
      >
        No data available
      </div>
    )
  }

  return (
    <LayoutRenderer
      blueprintCode={blueprintCode}
      config={config}
      data={data}
      parameters={effectiveParameters}
      latch={latch}
    />
  )
}

BlueprintChart.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  config: PropTypes.shape({
    layout: PropTypes.oneOf(['axis', 'pie', 'treemap', 'funnel', 'ring']).isRequired,
    purposeCode: PropTypes.string,
    height: PropTypes.number,
    colorTheme: PropTypes.string
  }).isRequired,
  latch: PropTypes.shape({
    parameterName: PropTypes.string.isRequired,
    dateColumn: PropTypes.string.isRequired,
    dateTable: PropTypes.string.isRequired,
    limit: PropTypes.number
  }),
  parameters: PropTypes.object
}

BlueprintChart.defaultProps = {
  parameters: {}
}

export default BlueprintChart
